<template>
  <div class="client-dashboard">
    <!-- Hero -->
    <section class="dashboard-hero">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-md-8">
            <h1 class="hero-title">
              Bienvenue, <span class="highlight">{{ firstName }}</span>
            </h1>
            <p class="hero-subtitle">
              Consultez l'activité de recrutement de <strong>{{ clientName }}</strong>
              et accédez aux profils présélectionnés par le cabinet.
            </p>
          </div>
          <div class="col-md-4 text-md-end d-none d-md-block">
            <div class="hero-illustration">
              <i class="fas fa-chart-line"></i>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="container py-5">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 text-muted">Chargement du tableau de bord...</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="alert alert-warning">
        <i class="fas fa-exclamation-triangle me-2"></i>{{ error }}
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Stats cards -->
        <div class="row g-4 mb-5">
          <div class="col-md-6 col-lg-3">
            <div class="stat-card stat-primary">
              <div class="stat-icon">
                <i class="fas fa-briefcase"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ stats.active_offers }}</div>
                <div class="stat-label">Offres actives</div>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-lg-3">
            <div class="stat-card stat-orange">
              <div class="stat-icon">
                <i class="fas fa-user-check"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ stats.preselected }}</div>
                <div class="stat-label">Candidats présélectionnés</div>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-lg-3">
            <div class="stat-card stat-green">
              <div class="stat-icon">
                <i class="fas fa-star"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ stats.selected }}</div>
                <div class="stat-label">Candidats retenus</div>
              </div>
            </div>
          </div>

          <div class="col-md-6 col-lg-3">
            <div class="stat-card stat-info">
              <div class="stat-icon">
                <i class="fas fa-clipboard-check"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ stats.tests_completed }}</div>
                <div class="stat-label">Tests complétés</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick actions -->
        <div class="row g-4 mb-5">
          <div class="col-lg-4">
            <router-link :to="{ name: 'ClientOffers' }" class="action-card">
              <div class="action-icon">
                <i class="fas fa-list"></i>
              </div>
              <h5>Voir toutes mes offres</h5>
              <p class="text-muted mb-0">Consultez le pipeline de chaque poste ouvert</p>
              <i class="fas fa-arrow-right action-arrow"></i>
            </router-link>
          </div>
        </div>

        <!-- Latest preselected -->
        <div class="section-header d-flex justify-content-between align-items-center mb-3">
          <h3 class="section-title">
            <i class="fas fa-user-check me-2 text-primary"></i>
            Derniers candidats présélectionnés
          </h3>
          <router-link
            v-if="latestPreselected.length"
            :to="{ name: 'ClientOffers' }"
            class="text-primary text-decoration-none"
          >
            Tout voir <i class="fas fa-arrow-right ms-1"></i>
          </router-link>
        </div>

        <div v-if="!latestPreselected.length" class="empty-state">
          <i class="fas fa-inbox"></i>
          <h5>Aucun candidat présélectionné pour le moment</h5>
          <p class="text-muted">
            Dès que le cabinet aura présélectionné des profils pour vos offres,
            ils apparaîtront ici.
          </p>
        </div>

        <div v-else class="row g-3">
          <div
            v-for="app in latestPreselected"
            :key="app.id"
            class="col-md-6 col-lg-4"
          >
            <router-link
              :to="{ name: 'ClientCandidateResults', params: { applicationId: app.id } }"
              class="candidate-preview-card"
            >
              <div class="candidate-avatar">
                {{ initials(app.candidate_name) }}
              </div>
              <div class="candidate-info">
                <div class="candidate-name">{{ app.candidate_name }}</div>
                <div class="candidate-job">{{ app.job_offer_title }}</div>
                <div class="candidate-meta">
                  <span
                    class="badge-tests"
                    :class="{ 'badge-done': app.all_tests_completed }"
                  >
                    <i
                      class="me-1"
                      :class="app.all_tests_completed ? 'fas fa-check-circle' : 'fas fa-hourglass-half'"
                    ></i>
                    {{ app.all_tests_completed ? 'Tests complétés' : 'Tests en cours' }}
                  </span>
                  <span class="candidate-date">
                    {{ formatDate(app.preselected_at) }}
                  </span>
                </div>
              </div>
              <i class="fas fa-chevron-right candidate-arrow"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import clientApi from '@/services/clientApi'

export default {
  name: 'ClientDashboard',
  inject: ['ToastError'],
  data() {
    return {
      loading: true,
      error: null,
      firstName: localStorage.getItem('firstName') || '',
      clientName: localStorage.getItem('client_name') || '',
      stats: {
        active_offers: 0,
        total_offers: 0,
        preselected: 0,
        selected: 0,
        tests_completed: 0,
      },
      latestPreselected: [],
    }
  },
  mounted() {
    this.loadDashboard()
  },
  methods: {
    async loadDashboard() {
      this.loading = true
      this.error = null
      try {
        const { data } = await clientApi.get('/client/dashboard')
        const payload = data.data || data
        this.stats = payload.stats || this.stats
        this.latestPreselected = payload.latest_preselected || []
      } catch (e) {
        this.error = 'Impossible de charger les données du tableau de bord.'
      } finally {
        this.loading = false
      }
    },
    initials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      const a = parts[0] ? parts[0].charAt(0) : ''
      const b = parts[1] ? parts[1].charAt(0) : ''
      return (a + b).toUpperCase()
    },
    formatDate(iso) {
      if (!iso) return ''
      try {
        const d = new Date(iso)
        return d.toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      } catch (e) {
        return ''
      }
    },
  },
}
</script>

<style scoped>
.client-dashboard {
  min-height: 100vh;
  background: #f8f9fa;
}

.dashboard-hero {
  background: linear-gradient(135deg, #2596be 0%, #29b3cd 100%);
  color: #fff;
  padding: 3.5rem 0;
  position: relative;
  overflow: hidden;
}

.dashboard-hero::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 400px;
  height: 400px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 50%;
}

.hero-title {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.hero-title .highlight {
  color: #ffd6a8;
}

.hero-subtitle {
  font-size: 1.1rem;
  opacity: 0.92;
  margin-bottom: 0;
}

.hero-illustration {
  width: 140px;
  height: 140px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 3.5rem;
  color: #fff;
  backdrop-filter: blur(8px);
}

.stat-card {
  background: #fff;
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #fff;
  flex-shrink: 0;
}

.stat-primary .stat-icon {
  background: linear-gradient(135deg, #2596be, #29b3cd);
}

.stat-orange .stat-icon {
  background: linear-gradient(135deg, #e95d16, #bc6a3c);
}

.stat-green .stat-icon {
  background: linear-gradient(135deg, #28a745, #20a179);
}

.stat-info .stat-icon {
  background: linear-gradient(135deg, #6f42c1, #b76ad4);
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
}

.stat-label {
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.action-card {
  display: block;
  background: #fff;
  border-radius: 14px;
  padding: 1.8rem;
  text-decoration: none;
  color: #2c3e50;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  border: 2px solid transparent;
  position: relative;
  transition: all 0.2s;
}

.action-card:hover {
  border-color: #2596be;
  transform: translateY(-2px);
  color: #2c3e50;
}

.action-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: rgba(37, 150, 190, 0.12);
  color: #2596be;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.action-arrow {
  position: absolute;
  top: 1.8rem;
  right: 1.8rem;
  color: #2596be;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.action-card:hover .action-arrow {
  opacity: 1;
}

.section-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0;
}

.empty-state {
  background: #fff;
  border-radius: 14px;
  padding: 3rem 1.5rem;
  text-align: center;
  color: #6c757d;
}

.empty-state i {
  font-size: 3rem;
  color: #dee2e6;
  margin-bottom: 1rem;
}

.candidate-preview-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #fff;
  border-radius: 14px;
  padding: 1.2rem;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  transition: all 0.2s;
  border: 2px solid transparent;
  position: relative;
}

.candidate-preview-card:hover {
  border-color: #2596be;
  transform: translateY(-2px);
  color: inherit;
}

.candidate-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2596be, #29b3cd);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
}

.candidate-info {
  flex: 1;
  min-width: 0;
}

.candidate-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
}

.candidate-job {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.4rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.candidate-meta {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.75rem;
}

.badge-tests {
  background: rgba(233, 93, 22, 0.12);
  color: #e95d16;
  padding: 2px 8px;
  border-radius: 8px;
  font-weight: 500;
}

.badge-tests.badge-done {
  background: rgba(40, 167, 69, 0.12);
  color: #28a745;
}

.candidate-date {
  color: #6c757d;
}

.candidate-arrow {
  color: #2596be;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.candidate-preview-card:hover .candidate-arrow {
  opacity: 1;
}
</style>
